import React from 'react';
import { FiGlobe, FiTrendingUp,  FiUsers, FiShield, FiClock, FiBookOpen, FiAward } from 'react-icons/fi';

const OnePlatform = () => {
  const reasons = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'India\'s Education System is Incredibly Diverse',
      description: 'From a small village school with 50 students to a sprawling university with 50,000 students—Indias education system is vast and varied. We have schools following CBSE, ICSE, state boards, and international curricula. We have PU colleges, degree colleges, engineering institutes, medical colleges, and more. A single ERP should be able to handle this diversity.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Educational Groups Need Consistency Across Institutions',
      description: 'Trusts and groups running multiple institution types need consistency—without sacrificing flexibility. They need a platform that provides a unified experience for administrators while adapting to the unique needs of schools, colleges, and universities.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Students Move Across Institution Types',
      description: 'A student starts in school, moves to PU college, then to degree college, and possibly to university. Their records, progress, and achievements should follow them seamlessly. A versatile ERP enables this continuity.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Administrative Complexity is Growing',
      description: 'Indian educational institutions face increasing administrative complexity—regulatory compliance, parent expectations, competition, technology adoption. A versatile ERP reduces this complexity instead of adding to it.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Data Security Cannot Be Compromised',
      description: 'With digitization comes responsibility. A versatile ERP provides enterprise-grade security across all institution types—protecting student data, staff records, and financial information. No compromises.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'The Future Requires Flexibility',
      description: 'Education is evolving. Hybrid learning, online assessments, skill-based education—the future is unpredictable. A versatile ERP can adapt to changing needs without requiring a complete overhaul.',
      color: 'bg-blue-50'
    }
  ];

  const institutionNeeds = [
    {
      type: 'Schools',
      needs: 'Parent communication, daily attendance, report cards, extracurricular tracking, simple interfaces for teachers',
      color: 'bg-indigo-100'
    },
    {
      type: 'PU Colleges',
      needs: 'Stream management (Science, Commerce, Arts), exam preparation tracking, subject choices, competitive exam integration',
      color: 'bg-emerald-100'
    },
    {
      type: 'Degree Colleges',
      needs: 'Course management, semester systems, complex timetables, multiple exam patterns, student access to records',
      color: 'bg-amber-100'
    },
    {
      type: 'Universities',
      needs: 'Affiliated college management, research management, PhD scholar tracking, international collaborations, complex administration',
      color: 'bg-rose-100'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        India is not one education system—it's many. We have schools that teach in 22 different languages. We have institutions following curricula from state boards, CBSE, ICSE, and international boards. We have PU colleges that prepare students for different streams. We have degree colleges and universities with their own unique structures. And we have trusts and groups that manage all of these institution types together.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For too long, educational technology in India has been fragmented. <strong className="text-slate-900">A separate ERP for schools. Another for colleges. Something else for universities.</strong> This fragmentation creates silos, duplicates effort, and makes it impossible for educational groups to get a unified view of their operations.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        What India needs is a versatile educational ERP—one platform that can handle the diversity of our education system.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">1.5M+</div>
          <div className="text-sm text-slate-500">Schools across India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80,000+</div>
          <div className="text-sm text-slate-500">Higher education institutions</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">100+</div>
          <div className="text-sm text-slate-500">Educational groups using Truyon</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why India Needs a Versatile Educational ERP</h2>

      {/* Reasons List */}
      {reasons.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Institution Needs Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Different Institutions Need</h3>
        <p className="text-slate-600 text-sm mb-4">Each institution type has unique needs. A versatile ERP must handle all of them.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {institutionNeeds.map((item, i) => (
            <div key={i} className={`${item.color} rounded-xl p-4 border`}>
              <h4 className="font-bold text-slate-900 text-sm">{item.type}</h4>
              <p className="text-xs text-slate-600 mt-1">{item.needs}</p>
            </div>
          ))}
        </div>
      </div>

      {/* The Truyon Solution Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">One Platform. All Institution Types. Complete Control.</h3>
        <p className="text-indigo-800 leading-relaxed mb-3">
          Truyon was built with India's diverse education system in mind. Not as a generic ERP, but as a platform designed specifically for Indian educational institutions—from schools to universities, from trusts to standalone colleges.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <div className="bg-white rounded-xl p-3 text-center border border-indigo-200">
            <FiBookOpen className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
            <span className="text-xs font-bold text-slate-900">Schools</span>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-indigo-200">
            <FiAward className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
            <span className="text-xs font-bold text-slate-900">PU Colleges</span>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-indigo-200">
            <FiUsers className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
            <span className="text-xs font-bold text-slate-900">Degree Colleges</span>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-indigo-200">
            <FiGlobe className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
            <span className="text-xs font-bold text-slate-900">Universities</span>
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Difference: Fragmented vs. Versatile</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Fragmented Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Different systems for different institution types</span></li>
              <li className="flex items-start gap-2">• <span>No data sharing across systems</span></li>
              <li className="flex items-start gap-2">• <span>Multiple training programs for staff</span></li>
              <li className="flex items-start gap-2">• <span>Higher costs and implementation time</span></li>
              <li className="flex items-start gap-2">• <span>No central visibility across the trust</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Versatile Truyon Platform</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One platform for all institution types</span></li>
              <li className="flex items-start gap-2">• <span>Real-time data sync across institutions</span></li>
              <li className="flex items-start gap-2">• <span>Single training program for all staff</span></li>
              <li className="flex items-start gap-2">• <span>Cost-effective and quick to implement</span></li>
              <li className="flex items-start gap-2">• <span>Complete visibility across the trust</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Bigger Picture */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">India Needs a Platform, Not a Product</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          India's education system is too diverse for a one-size-fits-all product. We need a platform that can adapt to different needs, different institution types, and different contexts. A platform that grows with you—whether you're a single school or a large trust with dozens of institutions.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          We need technology that understands the Indian context. That handles the complexity of our education system without adding more complexity. That empowers educators instead of overwhelming them.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Truyon is that platform.</strong> Built for India. Built for all institution types. Built for the future of education.
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">From Schools to Universities—One Platform Does It All</h3>
        <p className="text-indigo-800 leading-relaxed">
          India's education system is diverse, complex, and full of potential. The technology that serves it should be equally capable. A versatile educational ERP isn't a luxury—it's a necessity.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon gives you one platform for all your institutions. Schools, PU colleges, degree colleges, universities—all handled seamlessly. Consistent processes. Complete visibility. Effortless management.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Your institutions are diverse. Your ERP should be too.</strong>
        </p>
      </div>
    </>
  );
};

export default OnePlatform;